import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, X } from "lucide-react";

const DRIVES_URL = "https://vanrockdrives.com";
const GROUP_URL = "https://vanrockglobal.com";

const FinalDrivesContext = createContext({ openFinalDrives: () => {}, openGroupSite: () => {} });

export const useFinalDrives = () => useContext(FinalDrivesContext);

export const FinalDrivesProvider = ({ children }) => {
  const [variant, setVariant] = useState(null);
  const openFinalDrives = useCallback(() => setVariant("drives"), []);
  const openGroupSite = useCallback(() => setVariant("group"), []);
  const close = useCallback(() => setVariant(null), []);

  return (
    <FinalDrivesContext.Provider value={{ openFinalDrives, openGroupSite }}>
      {children}
      {variant && <FinalDrivesInterstitial variant={variant} onClose={close} />}
    </FinalDrivesContext.Provider>
  );
};

const FinalDrivesInterstitial = ({ variant = "drives", onClose }) => {
  const { t } = useTranslation();
  const isGroup = variant === "group";
  const url = isGroup ? GROUP_URL : DRIVES_URL;

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="final-drives-interstitial"
    >
      <div
        className="relative w-full max-w-2xl bg-white shadow-[0_40px_90px_rgba(0,0,0,0.45)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="final-drives-close"
          className="absolute right-4 top-4 z-10 text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        <div className="bg-[#0A0A0A] px-6 sm:px-10 pt-9 pb-7">
          <p className="font-mono-tech text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-gray-500">
            {t("fd.leaving", "You are leaving VANROCK Track Solutions")}
          </p>
          {isGroup ? (
            <img src="/vanrock-logo-word-white.png" alt="VANROCK" className="h-7 sm:h-8 w-auto mt-4" />
          ) : (
            <img
              src="/vanrock-drives-light.png"
              alt="VANROCK Drives"
              className="h-7 sm:h-8 w-auto mt-4"
            />
          )}
        </div>

        <div className="h-[4px] w-full bg-yellow-500" aria-hidden="true" />

        <div className="px-6 sm:px-10 py-9">
          <h2 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl leading-[0.95] text-gray-950">
            {isGroup
              ? t("ext.groupHeadline", "The corporate home of VANROCK")
              : t("fd.headline", "Largest Range of Final Drives in the World")}
          </h2>
          <p className="mt-5 text-sm sm:text-base text-gray-600 leading-relaxed max-w-xl">
            {isGroup
              ? t(
                  "ext.groupBody",
                  "You are heading to the VANROCK Group corporate website — the group's companies, divisions and global operations in one place."
                )
              : t(
                  "fd.body",
                  "Final drives live on our dedicated online store, VANROCK Drives — with instant stock availability, quotes, parts lists and worldwide shipping across thousands of machine models."
                )}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-drives-continue"
              className="flex-1 flex items-center justify-center gap-2 bg-yellow-500 text-black font-mono-tech text-xs sm:text-sm font-bold uppercase tracking-[0.2em] px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              {t("fd.continue", "Continue to the site")}
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </a>
            <button
              onClick={onClose}
              data-testid="final-drives-stay"
              className="flex-1 sm:flex-none flex items-center justify-center border border-gray-300 text-gray-950 font-mono-tech text-xs sm:text-sm font-bold uppercase tracking-[0.2em] px-8 py-4 hover:border-gray-950 transition-colors"
            >
              {t("fd.stay", "Stay here")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
